import { RefObject, useCallback, useEffect, useRef } from "react";

const TOOLTIP_WRAPPER_CLASS = "recharts-tooltip-wrapper";

/**
 * Fixes the Recharts tooltip flying in from (0, 0) on first hover.
 *
 * On the first hidden → visible transition the inline `transition` is
 * temporarily set to `none` so the tooltip jumps straight to the data-point
 * position. The original Recharts transition is restored one frame later so
 * subsequent moves between points keep the smooth default lerp.
 *
 * Handles late-mounting containers (e.g. behind loading states) by polling
 * the ref until the element is available.
 */
export function useTooltipAnimation(
  containerRef: RefObject<HTMLElement | null>,
): void {
  const cleanupRef = useRef<(() => void) | null>(null);

  const attach = useCallback(
    (container: HTMLElement) => {
      let wasVisible = false;
      let frameId: number | null = null;

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const target = mutation.target as HTMLElement;
          if (!target.classList.contains(TOOLTIP_WRAPPER_CLASS)) continue;

          const isVisible = target.style.visibility !== "hidden";

          if (isVisible && !wasVisible) {
            const original = target.style.transition;
            target.style.transition = "none";

            if (frameId) cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(() => {
              target.style.transition = original;
              frameId = null;
            });
          }

          wasVisible = isVisible;
        }
      });

      observer.observe(container, {
        attributes: true,
        attributeFilter: ["style"],
        subtree: true,
      });

      return () => {
        observer.disconnect();
        if (frameId) cancelAnimationFrame(frameId);
      };
    },
    [],
  );

  useEffect(() => {
    // If the container is already mounted, attach immediately
    if (containerRef.current) {
      cleanupRef.current = attach(containerRef.current);

      return () => {
        cleanupRef.current?.();
        cleanupRef.current = null;
      };
    }

    // Container not yet in DOM (e.g. behind a loading state).
    // Poll cheaply until the ref is populated.
    const interval = setInterval(() => {
      if (containerRef.current) {
        clearInterval(interval);
        cleanupRef.current = attach(containerRef.current);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [containerRef, attach]);
}
